/* eslint-disable no-nested-ternary */
/* eslint-disable react-hooks/exhaustive-deps */
import AutorenewIcon from '@mui/icons-material/Autorenew';
import InfoIcon from '@mui/icons-material/Info';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import {
  Alert,
  Box,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import { Button, Tooltip } from '@efexteam/components';
import clsx from 'clsx';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { WalletTypes } from '@constants/enums';
import { RATE_ROUNDING_DECIMALS } from '@efex-components/payables/PaymentModal/constants/catalogs';
import {
  COUNTRY_CURRENCY,
  Currency,
} from '../../../../../components/common/Form/config';
import useGetExchange from '../../../../../hooks/useGetExchange';
import useImmediateApiCall from '../../../../../hooks/useImmediateApiCall';
import SelectCurrencyOptions, {
  OnChangeCountryOptionsProps,
} from '../../../../../modules/wallets/SelectCurrencyOptions';
import clientSvc from '../../../../../services/client/clientSvc';
import { IWallet } from '../../../../../services/client/types';
import mixpanelSvc from '../../../../../services/mixpanel/mixpanelSvc';
import rateSvc from '../../../../../services/rate/rateSvc';
import {
  ExchangeResponse,
  IExchange,
} from '../../../../../services/rate/types';
import {
  DECIMALS_TO_SHOW,
  camelCaseMapData,
  formatCurrency,
  getCurrencySymbol,
} from '../../../../../utils/formater';
import { applyEfexRateRounding } from '../../../../../components/payables/PaymentModal/utils/promoterRateHelpers';
import { selectProfileData } from '../../../../profile/store/selectors';
import { LOW_CONVERTED_DECIMALS, MAX_CONVERTED_DECIMALS } from '../../config';
import { ConvertCurrencies } from '../constants';
import useValidateConversion from '../hooks/useValidateConversion';
import { WalletsStyles } from '../wallets.styles';

interface ISelectCurrenciesProps {
  wallets: IWallet[] | undefined;
  setConfirmedData: (data: ConfirmedDataProps) => void;
  setConversionData: (data: ConversionDataProps) => void;
  goNextStep: () => void;
}

export interface ConfirmedDataProps {
  amount: number;
  desAmount: number;
  currency: string;
  desCurrency: string;
  rateUuid: string;
  reverse: boolean;
}

export interface ConversionDataProps {
  amount: number;
  amountConverted: number;
}

type OriginType = 'currency' | 'desCurrency';

const DEBOUNCE_TIME = 500;
const RELOAD_RATES = 60000;

const SelectCurrencies = ({
  wallets,
  setConfirmedData,
  setConversionData,
  goNextStep,
}: ISelectCurrenciesProps) => {
  const { t } = useTranslation();
  const userData = useSelector(selectProfileData);

  const { validateUsdAmount } = useValidateConversion();

  const [timer, setTimer] = useState<number | undefined>(undefined);
  const [isReverConversion, setIsReverConversion] = useState<
    boolean | undefined
  >(undefined);
  const [currency, setCurrency] = useState<Currency>(COUNTRY_CURRENCY.MX);
  const [originValue, setOriginValue] = useState<OriginType>('currency');
  const [desCurrency, setDesCurrency] = useState<Currency>(
    COUNTRY_CURRENCY.USA
  );
  const [toConvert, setToConvert] = useState<string>('0');

  const [converted, setConverted] = useState<string>('0');
  const [counter, setCounter] = useState(RELOAD_RATES / 1000);
  const [currentRate, setCurrentRate] = useState<IExchange | undefined>(
    undefined
  );
  const [convertError, setConvertError] = useState<any>(undefined);
  const [loading, setLoading] = useState(false);
  const [walletAmountError, setWalletAmountError] = useState(false);
  const [selectedWallet, setSelectedWallet] = useState(
    wallets?.find((item) => item.currency.name === COUNTRY_CURRENCY.MX)
  );
  const [enableConvert, setEnableConvert] = useState(true);
  const {
    loading: loadingExchangeRate,
    data,
    retry: getExchange,
    // eslint-disable-next-line @typescript-eslint/no-shadow
  } = useImmediateApiCall(({ currency, desCurrency, enterpriseId }) =>
    rateSvc
      .getExchange({ currency, desCurrency, enterpriseId })
      .then((x) => camelCaseMapData<ExchangeResponse, IExchange>(x))
  );

  const { loading: loadingExchange, handleGetExchange } = useGetExchange({
    rateUuid: data?.reference ?? '',
  });

  const disabledButton = useMemo(
    () =>
      [
        isReverConversion && parseFloat(converted) < 1,
        !isReverConversion && parseFloat(toConvert) < 1,
        parseFloat(converted) <= 0 || parseFloat(toConvert) <= 0,
        currency === desCurrency,
        loading,
        walletAmountError,
        loadingExchange,
      ].some((item) => item),
    [
      converted,
      currency,
      desCurrency,
      loading,
      toConvert,
      walletAmountError,
      loadingExchange,
    ]
  );

  const validateConversionButton = (originAmount: number) => {
    setEnableConvert(
      !validateUsdAmount({
        destinationCurrency: desCurrency,
        destinationAmount: originAmount,
      })
    );
  };

  const convertCurrency = async (value: string, origin: OriginType) => {
    clearInterval(timer);
    try {
      if (origin === 'currency') {
        handleGetExchange({
          amountToConvert: Number(value).toFixed(2),
          reverse: false,
          onChangeResult: (val) => {
            setConverted(val.toString());
            validateConversionButton(val);
          },
        });
      } else {
        handleGetExchange({
          amountToConvert: Number(value).toFixed(2),
          reverse: true,
          onChangeResult: (val) => {
            setToConvert(val.toString());
            validateConversionButton(Number(value));
          },
        });
      }
    } catch (exchangeError) {
      console.error(exchangeError);
    }
  };

  const reloadRates = async () => {
    if (currency === desCurrency) return;
    const enterpriseId = userData?.company?.enterpriseId ?? '';
    const payload = {
      currency,
      desCurrency,
      enterpriseId,
    };
    await getExchange(payload);
    const amountToConvert = originValue === 'currency' ? toConvert : converted;
    convertCurrency(parseFloat(amountToConvert).toFixed(2), originValue);
  };

  const shouldPerformSearch = (value: string, origin: OriginType) => {
    let fixedAmount = value.replace(/[^\d.-]/g, '');
    if (fixedAmount.startsWith('0') && fixedAmount.length > 1) {
      fixedAmount = fixedAmount.substring(1);
    }
    if (Number.isNaN(fixedAmount)) {
      return;
    }
    setOriginValue(origin);
    if (origin === 'currency') {
      setIsReverConversion(false);
      setToConvert(fixedAmount);
      if (fixedAmount === '') {
        setConverted('0');
      }
    } else {
      setIsReverConversion(true);
      setConverted(fixedAmount);
      if (fixedAmount === '') {
        setToConvert('0');
      }
    }
    if (timer) {
      clearInterval(timer);
    }
    if (fixedAmount !== '') {
      setTimer(
        window.setTimeout(() => {
          convertCurrency(
            fixedAmount || parseFloat(toConvert).toFixed(2),
            origin
          );
        }, DEBOUNCE_TIME)
      );
    }
    // eslint-disable-next-line consistent-return
    return () => clearInterval(timer);
  };

  const swipeValues = () => {
    const copiedValues = { currency, desCurrency };
    setConverted('0');
    setToConvert('0');
    setCurrency(copiedValues.desCurrency);
    setDesCurrency(copiedValues.currency);
  };

  const convertToNewCurrency = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setConvertError(undefined);
    const ID = userData?.company?.enterpriseId ?? '';
    const amountToConvert =
      originValue === 'currency'
        ? parseFloat(toConvert)
        : parseFloat(converted);
    try {
      const payload = {
        rateUuid: data?.reference ?? '',
        enterpriseId: ID,
        amount: amountToConvert,
        reverse: false,
        currency,
        desCurrency,
      };
      if (originValue === 'currency') {
        payload.reverse = false;
      } else {
        payload.reverse = true;
      }
      const result = await clientSvc.convertWalletCurrencies(payload);
      setConversionData(result as unknown as ConversionDataProps);
      setLoading(false);
      setConfirmedData({
        ...payload,
        desCurrency,
        amount: parseFloat(toConvert),
        desAmount: parseFloat(converted),
      });
      if (!userData?.isTestUser) {
        mixpanelSvc.track('Conversion', {
          amount: parseFloat(toConvert),
          desAmount: parseFloat(converted),
          currency,
          desCurrency,
          userCountry: userData?.company.country,
          enterpriseId: ID,
        });
      }
      goNextStep();
    } catch (error) {
      console.error(error);
      setLoading(false);
      if (!userData?.isTestUser) {
        mixpanelSvc.track('Conversion Failed', {
          amount: toConvert ?? 0,
          currency,
          desCurrency,
          desAmount: converted ?? 0,
          enterpriseId: ID,
        });
      }
      setConvertError(error);
    }
  };

  const handleChangeCountry = (changeProps: OnChangeCountryOptionsProps) => {
    setConverted('0');
    setToConvert('0');
    if (changeProps.origin === 'from') {
      setCurrency(changeProps.value);
    } else {
      setDesCurrency(changeProps.value);
    }
  };

  const globalAccountCurrencies = useMemo(
    () =>
      wallets
        ?.filter(
          (item) => item.wallet.wallet_type === WalletTypes.GLOBAL_ACCOUNT
        )
        .map((walletData) => ({
          name: walletData.currency.name,
          id: walletData.currency.id,
        })) || [],
    [wallets]
  );

  const currentRateAmount = useMemo(() => {
    if (!currentRate?.buy) return '0';
    const baseRate = parseFloat(currentRate.buy);
    return applyEfexRateRounding(baseRate, currentRate.isMainCurrency).toFixed(
      RATE_ROUNDING_DECIMALS
    );
  }, [currentRate]);

  useEffect(() => {
    if (data) setCurrentRate(data);
  }, [data]);

  useEffect(() => {
    if (currency === desCurrency) return;
    if (timer) {
      clearInterval(timer);
    }
    const enterpriseId = userData?.company?.enterpriseId ?? '';

    getExchange({ currency, desCurrency, enterpriseId });
    setSelectedWallet(
      wallets?.filter(
        (item) =>
          item.currency.name === currency &&
          item.wallet.wallet_type === WalletTypes.GLOBAL_ACCOUNT
      )[0]
    );
  }, [currency, desCurrency]);

  useEffect(() => {
    if (counter <= 0) reloadRates();
    const counterControl =
      counter > 0
        ? setInterval(() => setCounter(counter - 1), 1000)
        : setCounter(RELOAD_RATES / 1000);
    return () => clearInterval(counterControl as any);
  }, [counter]);

  useEffect(() => {
    if (
      parseFloat(
        parseFloat(selectedWallet?.balance || '0').toFixed(DECIMALS_TO_SHOW)
      ) < parseFloat(toConvert)
    )
      setWalletAmountError(true);
    else setWalletAmountError(false);
  }, [toConvert]);

  return (
    <>
      <Typography variant="body1">{t('walletConvertMessage')}</Typography>
      <form className={WalletsStyles.stepsForm} onSubmit={convertToNewCurrency}>
        <TextField
          fullWidth
          size="small"
          sx={{
            '.MuiOutlinedInput-root': { paddingRight: '0' },
            marginTop: '1rem',
          }}
          label={t('walletConvertFrom', {
            origin: t(ConvertCurrencies[currency]),
          })}
          onChange={(e) => shouldPerformSearch(e.target.value, 'currency')}
          value={toConvert}
          InputLabelProps={{
            shrink: typeof toConvert !== 'undefined',
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <SelectCurrencyOptions
                  origin="from"
                  currency={currency}
                  desCurrency={desCurrency}
                  onChange={handleChangeCountry}
                  currenciesOptions={globalAccountCurrencies}
                />
              </InputAdornment>
            ),
            startAdornment: (
              <InputAdornment position="start">
                {getCurrencySymbol(currency)}
              </InputAdornment>
            ),
          }}
          error={
            walletAmountError ||
            (!isReverConversion && parseFloat(toConvert) < 1)
          }
          helperText={
            !isReverConversion && parseFloat(toConvert) < 1
              ? t('amountToConverHelper', {
                  currency,
                })
              : t('walletAmountConversionError', {
                  maxAmount: formatCurrency(selectedWallet?.balance),
                  currency,
                })
          }
        />
        <div className="flex flex-row flex-nowrap gap-4 justify-center items-center text-alert-color my-4">
          <IconButton
            aria-label="swipe"
            className={WalletsStyles.swipeButton}
            onClick={swipeValues}
          >
            <SwapVertIcon />
          </IconButton>
          <Typography variant="body1">
            {t('walletReverseConversion')}
          </Typography>
        </div>
        <TextField
          fullWidth
          size="small"
          autoFocus
          sx={{
            '.MuiOutlinedInput-root': { paddingRight: '0' },
            marginTop: '1rem',
          }}
          label={t('walletConvertTo', {
            destiny: t(ConvertCurrencies[desCurrency]),
          })}
          onChange={(e) => shouldPerformSearch(e.target.value, 'desCurrency')}
          value={converted}
          error={
            (isReverConversion && parseFloat(converted) < 1) || enableConvert
          }
          helperText={
            (isReverConversion && parseFloat(converted) < 1) || enableConvert
              ? t('amountToConverHelper', {
                  currency: desCurrency,
                })
              : ''
          }
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <SelectCurrencyOptions
                  origin="to"
                  currency={currency}
                  desCurrency={desCurrency}
                  onChange={handleChangeCountry}
                  currenciesOptions={globalAccountCurrencies}
                />
              </InputAdornment>
            ),
            startAdornment: (
              <InputAdornment position="start">
                {getCurrencySymbol(desCurrency)}
              </InputAdornment>
            ),
          }}
        />
        <Grid item xs={12} sx={{ margin: '1rem 0' }}>
          <Box className={WalletsStyles.rows}>
            <Typography variant="body1">{t('walletExchangeRate')}</Typography>
            {currentRate && !loadingExchangeRate && (
              <Box className={clsx(WalletsStyles.rows, 'gap-1')}>
                <Typography variant="h6">
                  $ {currentRate.exchangeLabel}
                </Typography>
                <Tooltip
                  title={
                    <>
                      <Typography variant="caption" component="p">
                        {t('walletCalculateDisclaimer')}
                      </Typography>
                      <Typography variant="caption" component="p">
                        {t('walletCalculateDisclaimerMessage', {
                          amount: currentRateAmount,
                          currency:
                            currentRate?.exchangeLabel?.split(' ')[
                              (currentRate?.exchangeLabel?.split(' ') ?? [])
                                .length - 1
                            ],
                        })}
                      </Typography>
                    </>
                  }
                  placement="top"
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </Box>
            )}
          </Box>
          <Box className={WalletsStyles.rows}>
            <Typography variant="body1">{t('walletWillUpdate')}</Typography>
            <Typography variant="h6">
              {t('walletSeconds', { seconds: counter })}
            </Typography>
          </Box>
          <Box className={WalletsStyles.rows}>
            <Typography variant="body1">{t('walletTotalToConvert')}</Typography>
            <Typography variant="h6">
              {formatCurrency(
                converted,
                MAX_CONVERTED_DECIMALS,
                LOW_CONVERTED_DECIMALS
              )}{' '}
              {desCurrency}
            </Typography>
          </Box>
          {convertError && (
            <Alert sx={{ margin: '1rem auto' }} severity="error">
              {t('errorRetryMessage')}
            </Alert>
          )}
          <Box className="flex justify-center items-center mt-3">
            <Button
              appearance="primary"
              type="submit"
              size="large"
              disabled={disabledButton || enableConvert}
            >
              {t('walletConvertButton')}
              {(loading || loadingExchange) && (
                <AutorenewIcon className="animate-spin" />
              )}
            </Button>
          </Box>
        </Grid>
      </form>
    </>
  );
};

export default SelectCurrencies;
